import React, { useState, useEffect, useRef } from 'react';
import { Search, Edit2, SortAsc, SortDesc, Plus, AlertTriangle } from 'lucide-react';



// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

//********Utils Functions - BEGIN ********

  const formatDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

  const sortAssignees = (assignees, sortConfig) => {
  if (!sortConfig.key) return assignees;

  return [...assignees].sort((a, b) => {
    let aVal = a[sortConfig.key] || '';
    let bVal = b[sortConfig.key] || '';

    // Handle date sorting
    if (sortConfig.key === 'created_date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else {
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();
    }

    if (aVal < bVal) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aVal > bVal) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

  const filterAssignees = (assignees, searchTerm) => {
  if (!searchTerm) return assignees;

  return assignees.filter(assignee =>
    assignee.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignee.raid_owner_user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (assignee.raid_owner_type && assignee.raid_owner_type.toLowerCase().includes(searchTerm.toLowerCase()))
  );
};

  const filterUsers = (usersList, userSearchText) => {
  if (!userSearchText) return [];

  const lowercaseSearchText = userSearchText.toLowerCase();
  return usersList.filter(
    user =>
      user.user_name.toLowerCase().includes(lowercaseSearchText) ||
      user.user_id.toLowerCase().includes(lowercaseSearchText)
  );
};

  const paginateData = (data, currentPage, itemsPerPage) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  return data.slice(startIndex, startIndex + itemsPerPage);
};

  const getTotalPages = (dataLength, itemsPerPage) => {
  return Math.ceil(dataLength / itemsPerPage);
};

  const getAssigneeTypeBadgeStyle = (assigneeType) => {
  const baseStyle = {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px'
  };

  switch (assigneeType) {
    case 'Primary':
      return {
        ...baseStyle,
        backgroundColor: '#e3f2fd',
        color: '#1976d2'
      };
    case 'Secondary':
      return {
        ...baseStyle,
        backgroundColor: '#e8f5e8',
        color: '#2e7d32'
      };
    default:
      return {
        ...baseStyle,
        backgroundColor: '#f5f5f5',
        color: '#666'
      };
  }
};
//********Utils Functions - END ********



const generateRaidLogDirectLink = (raidId, projectId) => {
  const baseUrl = window.location.origin;
  const currentPath = '/RaidLogManagement';

  return `${baseUrl}${currentPath}?project=${encodeURIComponent(projectId)}&raidId=${encodeURIComponent(raidId)}&action=edit`;
};



//********Assignees Dialog Components - BEGIN********

 const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

 const ConfirmationDialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={DialogStyles.title}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={DialogStyles.button}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};
//********Assignees Dialog Components - END ********


//********Add Assignees Dialog Components - BEGIN ********
const AddAssigneeDialog = ({
  isOpen,
  onClose,
  raidIdWithPrefix,
  userSearchText,
  onUserSearchChange,
  isDropdownOpen,
  setIsDropdownOpen,
  dropdownRef,
  isLoadingUsers,
  filteredUsers,
  onUserSelect,
  selectedAssigneeType,
  onAssigneeTypeChange,
  assigneeTypes,
  isLoadingAssigneeTypes,
  error,
  isLoading,
  onAddAssignee,
  canEdit,
  selectedUser
}) => {
  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>Add Assignee</h2>

        <div className="mb-6 p-4 border border-gray-300 rounded-md">
          <h3 className="text-base font-semibold mb-3">Adding assignee to: <strong>{raidIdWithPrefix || 'No RAID selected'}</strong></h3>
        </div>

        {/* User Selection with Typeahead */}
        <div className="relative mb-4" ref={dropdownRef}>
          <label className="block text-sm font-medium mb-1 text-gray-700">Select User <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={userSearchText}
            onChange={onUserSearchChange}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full p-2 border rounded h-10"
            placeholder="Search for a user..."
            required
          />
          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md overflow-auto border border-gray-300">
              {isLoadingUsers ? (
                <div className="text-center py-2 text-gray-500">Loading users...</div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div
                    key={user.user_id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => onUserSelect(user)}
                  >
                    <div className="font-medium">{user.user_name}</div>
                    <div className="text-sm text-gray-500">{user.user_id}</div>
                  </div>
                ))
              ) : userSearchText ? (
                <div className="text-center py-2 text-gray-500">No users found</div>
              ) : (
                <div className="text-center py-2 text-gray-500">Start typing to search users</div>
              )}
            </div>
          )}
        </div>

        {/* Assignee Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1 text-gray-700">Assignee Type <span className="text-red-500">*</span></label>
          <select
            value={selectedAssigneeType}
            onChange={onAssigneeTypeChange}
            className="w-full p-2 border rounded h-10"
            disabled={isLoadingAssigneeTypes}
            required
          >
            <option value="">Select Assignee Type</option>
            {isLoadingAssigneeTypes ? (
              <option value="" disabled>Loading assignee types...</option>
            ) : (
              assigneeTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))
            )}
          </select>
          {isLoadingAssigneeTypes && (
            <p className="text-xs text-gray-500 mt-1">Fetching available assignee types...</p>
          )}
        </div>

        {/* Error/Success message */}
        {error && (
          <div className={`mb-4 p-2 rounded ${
            error.toLowerCase().includes('successfully')
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-500'
          }`}>
            {error}
          </div>
        )}

        <div className="flex justify-center gap-4">
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#FFFDD0',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <div className="group relative">
            <button
              onClick={onAddAssignee}
              style={{
                padding: '8px 16px',
                backgroundColor: canEdit ? '#C8F4DE' : '#CCCCCC',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor:   'pointer'
              }}
              disabled={isLoading || !selectedUser || !selectedAssigneeType}
            >
              {isLoading ? 'Adding...' : 'Add Assignee'}
            </button>

            {/* Add Assignee button tooltip */}
            {(!canEdit || !selectedUser || !selectedAssigneeType) && (
              <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                {!canEdit ? "Insufficient permission" :
                 !selectedUser ? "Please select a user" :
                 !selectedAssigneeType ? "Please select assignee type" : ""}
                <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
//********Add Assignees Dialog Components - END ********


//********Edit Assignees Dialog Components - BEGIN ********
const EditAssigneeDialog = ({
  isOpen,
  onClose,
  raidIdWithPrefix,
  assigneeBeingEdited,
  newAssigneeType,
  onAssigneeTypeChange,
  assigneeTypes,
  isLoadingAssigneeTypes,
  error,
  isLoading,
  onEditAssignee,
  canEdit
}) => {
  if (!isOpen || !assigneeBeingEdited) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>Edit Assignee Type</h2>

        <div className="mb-6 p-4 border border-gray-300 rounded-md">
          <h3 className="text-base font-semibold mb-3">Editing assignee for: <strong>{raidIdWithPrefix || 'No RAID selected'}</strong></h3>
          <p className="mb-1"><strong>User:</strong> {assigneeBeingEdited.user_name}</p>
          <p className="mb-1"><strong>User ID:</strong> {assigneeBeingEdited.raid_owner_user_id}</p>
          <p><strong>Current Type:</strong> {assigneeBeingEdited.raid_owner_type || 'Not specified'}</p>
        </div>

        {/* Assignee Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1 text-gray-700">New Assignee Type <span className="text-red-500">*</span></label>
          <select
            value={newAssigneeType}
            onChange={onAssigneeTypeChange}
            className="w-full p-2 border rounded h-10"
            disabled={isLoadingAssigneeTypes}
            required
          >
            <option value="">Select Assignee Type</option>
            {isLoadingAssigneeTypes ? (
              <option value="" disabled>Loading assignee types...</option>
            ) : (
              assigneeTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))
            )}
          </select>
          {isLoadingAssigneeTypes && (
            <p className="text-xs text-gray-500 mt-1">Fetching available assignee types...</p>
          )}
        </div>

        {/* Error/Success message */}
        {error && (
          <div className={`mb-4 p-2 rounded ${
            error.toLowerCase().includes('successfully')
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-500'
          }`}>
            {error}
          </div>
        )}

        <div className="flex justify-center gap-4">
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#FFFDD0',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <div className="group relative">
            <button
              onClick={onEditAssignee}
              style={{
                padding: '8px 16px',
                backgroundColor: canEdit ? '#C8F4DE' : '#CCCCCC',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor:   'pointer'
              }}
              disabled={isLoading || !newAssigneeType}
            >
              {isLoading ? 'Updating...' : 'Update Assignee Type'}
            </button>

            {/* Update Assignee button tooltip */}
            {(!canEdit || !newAssigneeType) && (
              <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                {!canEdit ? "Insufficient permission" :
                 !newAssigneeType ? "Please select assignee type" : ""}
                <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
//********Edit Assignees Dialog Components - END ********


const ManageRaidLogAssignees = ({
  isOpen,
  onClose,
  corporateAccount,
  projectId,
  login_token,
  raidType,
  raidId,
  raidIdWithPrefix,
  canEdit,
  userId
}) => {
  // Main data states
  const [assignees, setAssignees] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState(new Set());
  const [filteredAssignees, setFilteredAssignees] = useState([]);

  // Permissions state
  const [permissions, setPermissions] = useState({
    canAddEdit: false,
    canRemove: false
  });

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(''); // Single error state for both success and error messages
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Dialog states
  const [isAddAssigneeDialogOpen, setIsAddAssigneeDialogOpen] = useState(false);
  const [isEditAssigneeDialogOpen, setIsEditAssigneeDialogOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);

  // User search states
  const [userSearchText, setUserSearchText] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const dropdownRef = useRef(null);

  // Assignee types states
  const [assigneeTypes, setAssigneeTypes] = useState([]);
  const [selectedAssigneeType, setSelectedAssigneeType] = useState('');
  const [isLoadingAssigneeTypes, setIsLoadingAssigneeTypes] = useState(false);

const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Edit states
  const [assigneeBeingEdited, setAssigneeBeingEdited] = useState(null);
  const [newAssigneeType, setNewAssigneeType] = useState('');

  // Computed values
  const sortedAssignees = React.useMemo(() =>
    sortAssignees(filteredAssignees, sortConfig),
    [filteredAssignees, sortConfig]
  );

  const totalPages = getTotalPages(sortedAssignees.length, itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAssignees = paginateData(sortedAssignees, currentPage, itemsPerPage);

  // Utility functions
  const resetAllData = () => {
    setAssignees([]);
    setSelectedAssignees(new Set());
    setError('');
    setIsLoading(false);
    setSearchTerm('');
    setFilteredAssignees([]);
    setCurrentPage(1);
  };

  // Dialog handlers
  const handleDialogClose = () => {
    setShowDialog(false);
    if (pendingDeleteAction) {
      pendingDeleteAction();
      setPendingDeleteAction(null);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
    setPendingDeleteAction(null);
  };

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Effects
  useEffect(() => {
  console.log('ManageRaidLogAssignees mounted with isOpen:', isOpen);
  console.log('raidId:', raidId);
    console.log('corporateAccount:', corporateAccount);
    console.log('projectId:', projectId);
    console.log('userId:', userId);

    if (isOpen && raidId && corporateAccount && projectId && userId) {
      resetAllData();
      checkPermission('Raid Log Management', ['Add or Edit Assignees', 'Remove Assignees']);
      loadAssignees();
      loadUsersList();
      loadAssigneeTypes();
    }
  }, [isOpen, raidId, corporateAccount, projectId, userId]);

  useEffect(() => {
    const filtered = filterAssignees(assignees, searchTerm);
    setFilteredAssignees(filtered);
    setCurrentPage(1);
  }, [searchTerm, assignees]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filtered = filterUsers(usersList, userSearchText);
    setFilteredUsers(filtered);
  }, [userSearchText, usersList]);

  // API calls
  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: projectId,
          category_header: categoryHeader,
          category_sub_header: categorySubHeaders
        }),
      });
      const data = await response.json();

      // Create new permissions object
      const newPermissions = { canAddEdit: false, canRemove: false };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Add or Edit Assignees':
            newPermissions.canAddEdit = item.has_access;
            break;
          case 'Remove Assignees':
            newPermissions.canRemove = item.has_access;
            break;
        }
      });

      setPermissions(newPermissions);
      console.log(`Permissions for ${categoryHeader}:`, newPermissions);
      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  const loadAssigneeTypes = async () => {
    setIsLoadingAssigneeTypes(true);
    const result = await fetchAssigneeTypes(corporateAccount, projectId, login_token);
    setIsLoadingAssigneeTypes(false);

    if (result.success) {
      setAssigneeTypes(result.data);
      if (result.data.length > 0) {
        const defaultType = result.data.find(type => type === 'Primary') || result.data[0];
        setSelectedAssigneeType(defaultType);
      }
    }
  };

//********Assignees API Functions - BEGIN********
 const fetchAssigneeTypes = async (corporateAccount, projectId, login_token) => {
  try {
    const response = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: projectId,
        entity: "RAID_OWNER_TYPE",
        search_query: ""
      })
    });

    const data = await response.json();

    if (data.status === 'Success') {
      const types = data.entity_statuses.map(item => item.status);
      return { success: true, data: types };
    } else {
      console.error('Failed to fetch assignee types:', data.status_description);
      // Fallback to default values if API fails
      return { success: true, data: ['Primary', 'Secondary', 'Other'] };
    }
  } catch (error) {
    console.error('Error fetching assignee types:', error);
    // Fallback to default values if API fails
    return { success: true, data: ['Primary', 'Secondary', 'Other'] };
  }
};

 const fetchAssignees = async (corporateAccount, projectId, login_token, raidType, raidId) => {
  if (!raidId || !raidType) {
    return { success: false, error: 'Missing RAID information' };
  }

  try {
    const response = await fetch(appConfig.getUrl('/get_raid_log_assignee_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: projectId,
        raid_type: raidType,
        raid_id: raidId
      })
    });

    const data = await response.json();

    if (data.status === 'Success') {
      return { success: true, data: data.assignee_list || [] };
    } else if (data.status === 'Failed' && data.status_description === 'No assignees found') {
      // Not necessarily an error if no assignees found
      return { success: true, data: [] };
    } else {
      return { success: false, error: data.status_description || 'Failed to fetch assignees' };
    }
  } catch (err) {
    console.error('Error fetching assignees:', err);
    return { success: false, error: err.message };
  }
};

 const fetchUsersList = async (corporateAccount, login_token) => {
  try {
    const response = await fetch(appConfig.getUrl('/get_user_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        "corporate_account": corporateAccount,
        "status": "Active"
      }),
    });

    const data = await response.json();

    if (data.status === 'Success') {
      return { success: true, data: data.user_list };
    } else {
      console.error('Failed to fetch users list:', data.status_description);
      return { success: false, error: data.status_description };
    }
  } catch (error) {
    console.error('Error fetching users list:', error);
    return { success: false, error: error.message };
  }
};

 const addAssignee = async (corporateAccount, projectId, login_token, raidType, raidId, userId, assigneeType) => {
  try {

      const directLink = generateRaidLogDirectLink(raidId, projectId);


    const response = await fetch(appConfig.getUrl('/add_raid_log_assignee'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: projectId,
        raid_type: raidType,
        raid_id: raidId,
        raid_owner_user_id: userId,
        raid_owner_type: assigneeType,
        item_direct_link: directLink

      })
    });

    const data = await response.json();

    if (data.status === 'Success') {
      return { success: true, data: data.status_description || 'Assignee added successfully' };
    } else {
      return { success: false, error: data.status_description || 'Failed to add assignee' };
    }
  } catch (err) {
    console.error('Error adding assignee:', err);
    return { success: false, error: err.message };
  }
};

 const deleteAssignee = async (corporateAccount, projectId, login_token, raidType, raidId, userId) => {
  try {


    const directLink = generateRaidLogDirectLink(raidId, projectId);


    const response = await fetch(appConfig.getUrl('/delete_raid_log_assignee'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: projectId,
        raid_type: raidType,
        raid_id: raidId,
        raid_owner_user_id: userId,
        item_direct_link: directLink
      })
    });

    const data = await response.json();

    if (data.status === 'Success') {
      return { success: true };
    } else {
      return { success: false, error: data.status_description || `Failed to remove assignee: ${userId}` };
    }
  } catch (apiError) {
    return { success: false, error: `API error: ${apiError.message}` };
  }



};

 const updateAssigneeTypeOld = async (corporateAccount, projectId, login_token, raidType, raidId, userId, newAssigneeType) => {
  try {
    // First, delete the existing assignee
    const deleteResult = await deleteAssignee(corporateAccount, projectId, login_token, raidType, raidId, userId);

    if (!deleteResult.success) {
      return { success: false, error: deleteResult.error || 'Failed to update assignee' };
    }

    // Then add the assignee back with the new type
    const addResult = await addAssignee(corporateAccount, projectId, login_token, raidType, raidId, userId, newAssigneeType);

    if (addResult.success) {
      return { success: true, data: 'Assignee type updated successfully' };
    } else {
      return { success: false, error: addResult.error || 'Failed to update assignee type' };
    }
  } catch (err) {
    console.error('Error updating assignee:', err);
    return { success: false, error: err.message };
  }
};


 const updateAssigneeTypeNew = async (corporateAccount, projectId, login_token, raidType, raidId, userId, newAssigneeType) => {
   try {


    const directLink = generateRaidLogDirectLink(raidId, projectId);


    const response = await fetch(appConfig.getUrl('/update_raid_log_assignee'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: projectId,
        raid_type: raidType,
        raid_id: raidId,
        raid_owner_user_id: userId,
        raid_owner_type: newAssigneeType,
        item_direct_link: directLink
      })
    });

    const data = await response.json();

    if (data.status === 'Success') {
            return { success: true, data: 'Assignee type updated successfully' };
    } else {
      return { success: false, error: data.status_description || ` Failed to update assignee type for ${userId}` };
    }
  } catch (apiError) {
    return { success: false, error: `API error: ${apiError.message}` };
  }




};




  const loadAssignees = async () => {
    setIsLoading(true);
    setError('');

    const result = await fetchAssignees(corporateAccount, projectId, login_token, raidType, raidId);
    setIsLoading(false);

    if (result.success) {
      setAssignees(result.data);
      setFilteredAssignees(result.data);
    } else {
//      setError(result.error);
    }
  };

  const loadUsersList = async () => {
    setIsLoadingUsers(true);
    const result = await fetchUsersList(corporateAccount, login_token);
    setIsLoadingUsers(false);

    if (result.success) {
      setUsersList(result.data);
    }
  };

  // User selection handlers
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setUserSearchText(user.user_name);
    setIsDropdownOpen(false);
  };

  const handleUserSearchChange = (e) => {
    setUserSearchText(e.target.value);
    setIsDropdownOpen(true);
    if (!e.target.value) {
      setSelectedUser(null);
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    // Search is handled by useEffect
  };

  // CRUD operations
  const handleAddAssignee = async () => {
    if (!selectedUser) {
      setError('Please select a user to add as assignee');
      return;
    }

    if (!selectedAssigneeType) {
      setError('Please select an assignee type');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await addAssignee(
      corporateAccount,
      projectId,
      login_token,
      raidType,
      raidId,
      selectedUser.user_id,
      selectedAssigneeType
    );

    if (result.success) {
      setError(`Successfully added ${selectedUser.user_name} as a ${selectedAssigneeType} assignee`);
      setSelectedUser(null);
      setUserSearchText('');
      setIsAddAssigneeDialogOpen(false);

      // Clear success message and refresh data after delay
      setTimeout(() => {
        setError('');
        loadAssignees();
      }, 2000);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  const handleEditAssignee = async () => {
    if (!assigneeBeingEdited) {
      setError('No assignee selected for editing');
      return;
    }

    if (!newAssigneeType) {
      setError('Please select a new assignee type');
      return;
    }

    if (newAssigneeType === assigneeBeingEdited.raid_owner_type) {
      setIsEditAssigneeDialogOpen(false);
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await updateAssigneeTypeNew(
      corporateAccount,
      projectId,
      login_token,
      raidType,
      raidId,
      assigneeBeingEdited.raid_owner_user_id,
      newAssigneeType
    );

    if (result.success) {
      setError(`Successfully updated ${assigneeBeingEdited.user_name} to ${newAssigneeType}`);
      setIsEditAssigneeDialogOpen(false);
      setAssigneeBeingEdited(null);
      setNewAssigneeType('');

      // Clear success message and refresh data after delay
      setTimeout(() => {
        setError('');
        loadAssignees();
      }, 2000);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };







 // Replace the existing handleDelete function with these two:

// Updated delete confirmation handler
const handleDeleteSelectedConfirm = () => {
  if (selectedAssignees.size === 0) {
    setError('No assignees selected for removal');
    return;
  }
  setIsDeleteConfirmOpen(true);
};

// Updated delete function
const handleDelete = async () => {
  setIsDeleteConfirmOpen(false);
  setIsLoading(true);
  setError('');

  let successCount = 0;
  let hasErrors = false;

  for (const userId of selectedAssignees) {
    const result = await deleteAssignee(corporateAccount, projectId, login_token, raidType, raidId, userId);

    if (result.success) {
      successCount++;
    } else {
      hasErrors = true;
      setError(result.error);
      break;
    }
  }

  if (!hasErrors) {
    setError(`Successfully removed ${successCount} assignee(s)`);
    setSelectedAssignees(new Set());

    // Clear success message and refresh data after delay
    setTimeout(() => {
      setError('');
      loadAssignees();
    }, 2000);
  }

  setIsLoading(false);
};










// New improved delete confirmation dialog
const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen) return null;

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '500px', padding: '0'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 20px',
          borderRadius: '8px 8px 0 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
            Are you sure you want to remove {selectedAssignees.size} selected assignee{selectedAssignees.size !== 1 ? 's' : ''}?
          </p>
          <p style={{ fontSize: '12px', color: '#dc2626', marginBottom: '16px' }}>
            This action cannot be undone.
          </p>

          {/* Show some details about selected assignees */}
          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px',
            maxHeight: '120px',
            overflowY: 'auto',
            border: '1px solid #e5e7eb'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Selected assignees:</p>
            {Array.from(selectedAssignees).slice(0, 5).map(userId => {
              const assignee = assignees.find(a => a.raid_owner_user_id === userId);
              return assignee ? (
                <div key={userId} style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                  <span style={{
                    padding: '1px 6px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    backgroundColor: assignee.raid_owner_type === 'Primary' ? '#e3f2fd' : '#e8f5e8',
                    color: assignee.raid_owner_type === 'Primary' ? '#1976d2' : '#2e7d32',
                    marginRight: '6px'
                  }}>
                    {assignee.raid_owner_type || 'N/A'}
                  </span>
                  {assignee.user_name} ({assignee.raid_owner_user_id})
                </div>
              ) : null;
            })}
            {selectedAssignees.size > 5 && (
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                ... and {selectedAssignees.size - 5} more
              </div>
            )}
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          padding: '0 20px 20px 20px'
        }}>
          <button
            onClick={() => setIsDeleteConfirmOpen(false)}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Remove {selectedAssignees.size} Assignee{selectedAssignees.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};














  // Dialog handlers
  const handleAddAssigneeClick = () => {
    if (!permissions.canAddEdit) {
      return;
    }
    setSelectedUser(null);
    setUserSearchText('');
    setError('');
    setIsAddAssigneeDialogOpen(true);

    if (usersList.length === 0) {
      loadUsersList();
    }
    if (assigneeTypes.length === 0) {
      loadAssigneeTypes();
    }
  };

  const handleEditAssigneeClick = (assignee) => {
    if (!permissions.canAddEdit) {
      return;
    }
    setAssigneeBeingEdited(assignee);
    setNewAssigneeType(assignee.raid_owner_type || '');
    setError('');
    setIsEditAssigneeDialogOpen(true);

    if (assigneeTypes.length === 0) {
      loadAssigneeTypes();
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedAssignees.size === paginatedAssignees.length) {
      setSelectedAssignees(new Set());
    } else {
      const newSelected = new Set(paginatedAssignees.map(assignee => assignee.raid_owner_user_id));
      setSelectedAssignees(newSelected);
    }
  };

  const toggleRowSelection = (userId) => {
    const newSelected = new Set(selectedAssignees);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedAssignees(newSelected);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95vw',
        maxWidth: '1400px',
        height: '70vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>

        {/* Header with title and search */}
        <div style={{
          backgroundColor: '#c8d7f0',
          padding: '12px 20px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="text"
              placeholder="Search assignees..."
              value={searchTerm}
              onChange={handleSearchInputChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                width: '250px'
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                padding: '8px 12px',
                backgroundColor: '#4285f4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Search size={16} />
            </button>
          </div>

          <h1 style={{
            flex: 1,
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#4285f4',
            margin: 0
          }}>
            Manage RAID Log Assignees - {raidIdWithPrefix}
          </h1>

          <div className="group relative">
            <button
              onClick={handleAddAssigneeClick}
              disabled={!permissions.canAddEdit || !raidId}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4285f4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: permissions.canAddEdit && raidId ? 1 : 0.6
              }}
            >
              <Plus size={16} />
              Add Assignee
            </button>

            {/* Add Assignee button tooltip */}
            {(!permissions.canAddEdit || !raidId) && (
              <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                {!permissions.canAddEdit ? "Insufficient permission" : "No RAID selected"}
                <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
        </div>

        {/* Simple Header Bar */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{ padding: '12px 20px' }}>
            <div className={`mb-4 p-2 rounded ${
              error.toLowerCase().includes('successfully')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-500'
            }`}>
              {error}
            </div>
          </div>
        )}

        {/* Table Container */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={paginatedAssignees.length > 0 && selectedAssignees.size === paginatedAssignees.length}
                    onChange={toggleSelectAll}
                    disabled={paginatedAssignees.length === 0}
                    style={{ marginRight: '8px' }}
                  />
                  Select All
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('user_name')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    User Name
                    {sortConfig.key === 'user_name' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('raid_owner_user_id')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    User ID
                    {sortConfig.key === 'raid_owner_user_id' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('raid_owner_type')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Assignee Type
                    {sortConfig.key === 'raid_owner_type' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('created_date')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Assigned Date
                    {sortConfig.key === 'created_date' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    Loading...
                  </td>
                </tr>
              ) : paginatedAssignees.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    No assignees found. Add a new assignee or adjust your search.
                  </td>
                </tr>
              ) : (
                paginatedAssignees.map((assignee, index) => (
                  <tr
                    key={assignee.raid_owner_user_id}
                    style={{
                      backgroundColor: selectedAssignees.has(assignee.raid_owner_user_id) ? '#e3f2fd' : (index % 2 === 0 ? '#fafafa' : 'white'),
                      borderBottom: '1px solid #dee2e6'
                    }}
                  >
                    <td style={{ padding: '12px 8px' }}>
                      <input
                        type="checkbox"
                        checked={selectedAssignees.has(assignee.raid_owner_user_id)}
                        onChange={() => toggleRowSelection(assignee.raid_owner_user_id)}
                      />
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '500' }}>
                      {assignee.user_name}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>
                      {assignee.raid_owner_user_id}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '12px' }}>
                      {assignee.raid_owner_type && (
                        <span style={getAssigneeTypeBadgeStyle(assignee.raid_owner_type)}>
                          {assignee.raid_owner_type}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '12px' }}>
                      {formatDate(assignee.created_date)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div className="group relative">
                        <button
                          onClick={() => handleEditAssigneeClick(assignee)}
                          disabled={!permissions.canAddEdit}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: permissions.canAddEdit ? '#4285f4' : '#ccc',
                            cursor:   'pointer',
                            padding: '4px'
                          }}
                        >
                          <Edit2 size={16} />
                        </button>

                        {/* Edit button tooltip */}
                        {!permissions.canAddEdit && (
                          <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                            Insufficient permission
                            <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && sortedAssignees.length > 0 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            fontSize: '14px'
          }}>
            <div style={{ color: '#666' }}>
              Total Entries: {sortedAssignees.length} | Showing {sortedAssignees.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedAssignees.length)} of {sortedAssignees.length}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  color: '#4285f4'
                }}
              >
                ‹
              </button>
              <span style={{ color: '#4285f4' }}>
                Page {currentPage} of {Math.max(1, totalPages)}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{
                  padding: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor:  'pointer',
                  opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1,
                  color: '#4285f4'
                }}
              >
                ›
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div className="group relative">
            <button
              onClick={handleDeleteSelectedConfirm}
              disabled={isLoading || selectedAssignees.size === 0 || !permissions.canRemove}
              style={{
                padding: '8px 16px',
                backgroundColor: selectedAssignees.size > 0 && !isLoading && permissions.canRemove ? '#dc3545' : '#ff91ad',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor:  'pointer'
              }}
            >
              {isLoading ? 'Removing...' : `Remove Selected (${selectedAssignees.size})`}
            </button>

            {/* Delete button tooltip */}
            {(selectedAssignees.size === 0 || !permissions.canRemove) && (
              <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                {selectedAssignees.size === 0
                  ? "Select one or more items to delete"
                  : "Insufficient permission"
                }
                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200"></div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {selectedAssignees.size > 0 && (
              <span style={{ fontSize: '14px', color: '#666' }}>
                {selectedAssignees.size} selected
              </span>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#FFFDD0',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {showDialog && (
        <ConfirmationDialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}

      <AddAssigneeDialog
        isOpen={isAddAssigneeDialogOpen}
        onClose={() => setIsAddAssigneeDialogOpen(false)}
        raidIdWithPrefix={raidIdWithPrefix}
        userSearchText={userSearchText}
        onUserSearchChange={handleUserSearchChange}
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={setIsDropdownOpen}
        dropdownRef={dropdownRef}
        isLoadingUsers={isLoadingUsers}
        filteredUsers={filteredUsers}
        onUserSelect={handleUserSelect}
        selectedAssigneeType={selectedAssigneeType}
        onAssigneeTypeChange={(e) => setSelectedAssigneeType(e.target.value)}
        assigneeTypes={assigneeTypes}
        isLoadingAssigneeTypes={isLoadingAssigneeTypes}
        error={error}
        isLoading={isLoading}
        onAddAssignee={handleAddAssignee}
        canEdit={permissions.canAddEdit}
        selectedUser={selectedUser}
      />

      <EditAssigneeDialog
        isOpen={isEditAssigneeDialogOpen}
        onClose={() => setIsEditAssigneeDialogOpen(false)}
        raidIdWithPrefix={raidIdWithPrefix}
        assigneeBeingEdited={assigneeBeingEdited}
        newAssigneeType={newAssigneeType}
        onAssigneeTypeChange={(e) => setNewAssigneeType(e.target.value)}
        assigneeTypes={assigneeTypes}
        isLoadingAssigneeTypes={isLoadingAssigneeTypes}
        error={error}
        isLoading={isLoading}
        onEditAssignee={handleEditAssignee}
        canEdit={permissions.canAddEdit}
      />




      {/* Improved Delete Confirmation Dialog */}
    {renderDeleteConfirmDialog()}



    </div>
  );
};

export default ManageRaidLogAssignees;